'use client';

import React from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '@heroui/modal';
import { CaseStudy } from '@/data/case-studies';
import {
  Code,
  Activity,
  Network,
  Shield,
  Cpu,
  TrendingUp,
  HelpCircle,
  Database,
  HousePlug,
  Barcode,
  CarFront,
} from 'lucide-react';

interface CaseStudyModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseStudy: CaseStudy;
}

const CaseStudyModal: React.FC<CaseStudyModalProps> = ({
  isOpen,
  onClose,
  caseStudy,
}) => {
  if (!caseStudy) return null;

  // Helper function to render Lucide React icons
  const renderIcon = (iconName: string) => {
    // Map icon names to components
    const iconMap: Record<
      string,
      React.ComponentType<{ className?: string }>
    > = {
      Code: Code,
      Activity: Activity,
      Network: Network,
      Shield: Shield,
      Cpu: Cpu,
      Database: Database,
      TrendingUp: TrendingUp,
      Barcode: Barcode,
      HousePlug: HousePlug,
      CarFront: CarFront,
    };

    const IconComponent = iconMap[iconName];

    if (IconComponent) {
      return <IconComponent className="h-5 w-5 text-white sm:h-6 sm:w-6" />;
    }
    // Fallback to a default icon if the icon name is not found
    return <HelpCircle className="h-5 w-5 text-white sm:h-6 sm:w-6" />;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      backdrop="blur"
      scrollBehavior="inside"
      placement="center"
      classNames={{
        backdrop: 'bg-black/60 backdrop-blur-sm',
        base: 'border border-border/30 bg-background rounded-3xl shadow-2xl max-h-[90vh] flex flex-col',
        header: 'border-b border-border/20 pb-0 shrink-0',
        body: 'p-0 overflow-hidden flex-1',
        closeButton: 'hover:bg-muted/50 right-4 top-4 text-foreground',
      }}
    >
      <ModalContent className="bg-background mx-auto max-h-[85vh] max-w-6xl overflow-hidden">
        <ModalHeader
          className={`flex flex-col gap-2 overflow-hidden rounded-t-2xl bg-gradient-to-r ${caseStudy.gradient.from} ${caseStudy.gradient.to} shrink-0 p-3 md:p-4`}
        >
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/30 bg-white/20 backdrop-blur-sm sm:h-11 sm:w-11">
                {renderIcon(caseStudy.icon)}
              </div>
              <div className="flex flex-col gap-1">
                <span className="inline-block rounded-full border border-white/30 bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">
                  {caseStudy.category}
                </span>
                <div className="text-base leading-tight font-bold text-white sm:text-lg md:text-xl">
                  {caseStudy.title}
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-white/90 sm:text-sm md:text-base">
            {caseStudy.description}
          </p>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
            {caseStudy.stats.map((stat, index) => (
              <div
                key={index}
                className="rounded-md border border-white/20 bg-white/10 p-2 backdrop-blur-sm sm:p-2.5"
              >
                <div className="text-xs text-white/80">{stat.label}</div>
                <div className="text-sm font-bold text-white sm:text-base">
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </ModalHeader>

        <ModalBody className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted/30 hover:scrollbar-thumb-muted/50 flex-1 overflow-y-auto">
          <div className="space-y-4 p-4 sm:p-6 md:p-8">
            {/* Problem Statement */}
            <section>
              <div className="mt-6 mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-red-600 dark:text-red-400"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h2 className="text-foreground text-2xl font-bold">
                  Problem Statement
                </h2>
              </div>
              <div className="bg-muted/30 border-border rounded-xl border p-4 sm:p-6">
                <p className="text-muted-foreground text-base leading-relaxed sm:text-lg">
                  {caseStudy.problemStatement}
                </p>
              </div>
            </section>

            {/* Solution Approach */}
            <section>
              <div className="mt-12 mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-blue-600 dark:text-blue-400"
                  >
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                  </svg>
                </div>
                <h2 className="text-foreground text-2xl font-bold">
                  Solution Approach
                </h2>
              </div>
              <div className="bg-muted/30 border-border rounded-xl border p-4 sm:p-6">
                <p className="text-muted-foreground text-base leading-relaxed sm:text-lg">
                  {caseStudy.solutionApproach}
                </p>
              </div>
            </section>

            {/* Challenges */}
            <section>
              <div className="mt-12 mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-orange-600 dark:text-orange-400"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                </div>
                <h2 className="text-foreground text-2xl font-bold">
                  Challenges Faced
                </h2>
              </div>
              <div className="grid gap-4">
                {caseStudy.challenges.map((challenge, index) => (
                  <div
                    key={index}
                    className="bg-muted/20 border-border/50 hover:bg-muted/30 flex items-start gap-3 rounded-xl border p-4 transition-colors"
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500/20">
                      <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                        {index + 1}
                      </span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {challenge}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Results */}
            <section>
              <div className="mt-12 mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-green-600 dark:text-green-400"
                  >
                    <line x1="12" y1="20" x2="12" y2="10"></line>
                    <line x1="18" y1="20" x2="18" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="16"></line>
                  </svg>
                </div>
                <h2 className="text-foreground text-2xl font-bold">
                  Results Achieved
                </h2>
              </div>
              <div className="grid gap-4">
                {caseStudy.results.map((result, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 rounded-xl border border-green-200/50 bg-green-50/50 p-4 dark:border-green-800/30 dark:bg-green-900/10"
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500">
                      <svg
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4 text-white"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <p className="text-foreground leading-relaxed font-medium">
                      {result}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Lessons Learned */}
            <section>
              <div className="mt-12 mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-purple-600 dark:text-purple-400"
                  >
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                </div>
                <h2 className="text-foreground text-2xl font-bold">
                  Lessons Learned
                </h2>
              </div>
              <div className="grid gap-4">
                {caseStudy.lessonsLearned.map((lesson, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 rounded-xl border border-purple-200/50 bg-purple-50/50 p-4 dark:border-purple-800/30 dark:bg-purple-900/10"
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500/20">
                      <span className="text-sm text-purple-600 dark:text-purple-400">
                        💡
                      </span>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {lesson}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Technology Stack */}
            <section>
              <div className="mt-12 mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <svg
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 text-blue-600 dark:text-blue-400"
                  >
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                </div>
                <h2 className="text-foreground text-2xl font-bold">
                  Technology Stack
                </h2>
              </div>
              <div className="bg-muted/20 border-border rounded-xl border p-6">
                <div className="flex flex-wrap gap-3">
                  {caseStudy.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="from-primary/10 text-secondary border-primary/20 hover:bg-primary/20 rounded-lg border bg-gradient-to-r to-blue-500/10 px-4 py-2 text-sm font-medium transition-colors"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            {/* Business Impact */}
            <section className="border-border border-t pt-8">
              <div className="from-primary/10 border-primary/20 rounded-2xl border bg-gradient-to-r to-blue-500/10 p-8">
                <div className="mb-4 flex items-center gap-3">
                  <div className="bg-primary/20 flex h-10 w-10 items-center justify-center rounded-xl">
                    <svg
                      stroke="currentColor"
                      fill="none"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-secondary h-5 w-5"
                    >
                      <line x1="12" y1="1" x2="12" y2="23"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                  </div>
                  <h2 className="text-foreground text-2xl font-bold">
                    Business Impact
                  </h2>
                </div>
                <p className="text-secondary text-xl leading-relaxed font-semibold">
                  {caseStudy.businessImpact}
                </p>
              </div>
            </section>
          </div>
        </ModalBody>

        <ModalFooter className="border-border/50 bg-muted/20 shrink-0 border-t px-4 py-4 sm:px-6 sm:py-6">
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-muted-foreground text-xs sm:text-sm">
              <span className="text-foreground font-semibold">
                Technologies:
              </span>{' '}
              {caseStudy.technologies.join(', ')}
            </div>
            <button
              onClick={onClose}
              aria-label="Close case study modal"
              className="from-primary rounded-lg bg-gradient-to-r to-blue-500 px-4 py-2 text-sm font-semibold text-white transition-all duration-300 hover:scale-105 sm:px-6 sm:text-base"
            >
              Close Case Study
            </button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CaseStudyModal;
